/**
 * ZirkelOverlay – Stilisierter Zirkel (Kompass) für Kreisbögen.
 * Physik: 1) Nadel positionieren (bleibt fix = Mittelpunkt des Kreises).
 * 2) Bleistift verschieben = Bogenanfang festlegen (Radius = Abstand Nadel–Bleistift, Schenkel öffnen/schließen).
 * 3) Am Griff (wo die Schenkel zusammenlaufen) drehen = ganzer Zirkel um die Nadel rotieren, Kreisbogen zeichnen.
 */
import { useRef, useState, useCallback } from 'react';

export interface ZirkelState {
  visible: boolean;
  x: number;
  y: number;
  radius: number;
  /** Länge beider Schenkel (Nadel–Griff und Griff–Bleistift); bestimmt den Winkel zwischen den Schenkeln. */
  legLength: number;
}

export const DEFAULT_ZIRKEL: ZirkelState = {
  visible: false,
  x: 400,
  y: 400,
  radius: 120,
  legLength: 90,
};

const RADIUS_MIN = 20;
const RADIUS_MAX = 500;
export const LEG_LENGTH_MIN = 30;
export const LEG_LENGTH_MAX = 350;
/** Dicke der Schenkel für plastische Darstellung (Pixel in Viewport). */
const LEG_WIDTH = 5;
/** Abstand der Rechenkaro-Kästchen in Display-Pixel (1 Kästchen = 40). */
const GRID_CELL_PX = 40;

/** Kurve entlang des Kreisumfangs in der tatsächlichen Drehrichtung (0° = rechts, 90° = unten). Kein Kürzen auf <180°, damit bei >180° kein Flip. */
export function arcPoints(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
  stepsPerDeg = 2
): Array<{ x: number; y: number }> {
  const d = endDeg - startDeg;
  const n = Math.max(8, Math.ceil(Math.abs(d) * stepsPerDeg));
  const points: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= n; i++) {
    const deg = startDeg + d * (i / n);
    const rad = (deg * Math.PI) / 180;
    points.push({
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad),
    });
  }
  return points;
}

interface ZirkelOverlayProps {
  x: number;
  y: number;
  radius: number;
  legLength: number;
  displayScale?: number;
  interactive?: boolean;
  onMove?: (x: number, y: number) => void;
  onRadiusChange?: (radius: number) => void;
  /** Wird nach dem Ziehen des Bogen-Handles mit (cx, cy, radius, startDeg, endDeg) aufgerufen. */
  onDrawArc?: (cx: number, cy: number, radius: number, startDeg: number, endDeg: number) => void;
  /** Wird während des Drehens mit dem Griff aufgerufen (Live-Vorschau); mit null beim Loslassen. */
  onArcPreview?: (preview: { cx: number; cy: number; radius: number; startDeg: number; endDeg: number } | null) => void;
  /** Optional: Magnet-Snap für Bleistiftposition (Rechenkaro-Kreuzungen). */
  snapToGrid?: (x: number, y: number) => { x: number; y: number };
  strokeColor?: string;
}

function pointerAngleDeg(px: number, py: number, cx: number, cy: number): number {
  const deg = (Math.atan2(py - cy, px - cx) * 180) / Math.PI;
  return deg < 0 ? deg + 360 : deg;
}

export function ZirkelOverlay({
  x,
  y,
  radius,
  legLength,
  displayScale = 1,
  interactive = true,
  onMove,
  onRadiusChange,
  onDrawArc,
  onArcPreview,
  snapToGrid,
  strokeColor = 'rgba(255,255,255,0.85)',
}: ZirkelOverlayProps) {
  const [dragMode, setDragMode] = useState<'center' | 'radius' | 'arc' | null>(null);
  /** Winkel der Bleistiftspitze (0° = rechts); = Startpunkt des Bogens, Schenkelrichtung. */
  const [radiusHandleAngle, setRadiusHandleAngle] = useState(0);
  const arcStartAngleRef = useRef(0);
  /** Beim Griff-Down: Klickposition; Winkel erst nach kleiner Bewegung aktualisieren. */
  const arcDragStartClientRef = useRef<{ x: number; y: number } | null>(null);
  /** Nach Überschreiten der Schwelle: Zirkel- und Zeigerwinkel zum Start. */
  const arcDragInitialRef = useRef<{ compassAngle: number; pointerAngle: number } | null>(null);
  /** Kumulierte Zeiger-Drehung (ungewrappt), damit >360° und >270° keinen Sprung verursachen. */
  const arcDragUnwrappedRef = useRef<number>(0);
  const arcDragPrevPointerRef = useRef<number>(0);
  /** Letzter Endwinkel (ungewrappt) für Commit beim Loslassen. */
  const arcEndUnwrappedRef = useRef<number>(0);
  const dragStartRef = useRef<{
    clientX: number;
    clientY: number;
    x: number;
    y: number;
    radius: number;
    centerClientX?: number;
    centerClientY?: number;
    initialDist?: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getCenterClient = useCallback((): { cx: number; cy: number } => {
    const rect = containerRef.current?.getBoundingClientRect();
    return {
      cx: rect ? rect.left + rect.width / 2 : 0,
      cy: rect ? rect.top + rect.height / 2 : 0,
    };
  }, []);

  const handleCenterDown = useCallback(
    (e: React.PointerEvent) => {
      if (!interactive || !onMove) return;
      e.preventDefault();
      e.stopPropagation();
      setDragMode('center');
      dragStartRef.current = { clientX: e.clientX, clientY: e.clientY, x, y, radius };
      try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ok */ }
    },
    [interactive, onMove, x, y, radius]
  );

  const handleRadiusDown = useCallback(
    (e: React.PointerEvent) => {
      if (!interactive || !onRadiusChange) return;
      e.preventDefault();
      e.stopPropagation();
      const { cx: centerClientX, cy: centerClientY } = getCenterClient();
      setDragMode('radius');
      dragStartRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        x,
        y,
        radius,
        centerClientX,
        centerClientY,
      };
      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ok */ }
    },
    [interactive, onRadiusChange, x, y, radius, getCenterClient]
  );

  const handleArcDown = useCallback(
    (e: React.PointerEvent) => {
      if (!interactive || !onDrawArc) return;
      e.preventDefault();
      e.stopPropagation();
      arcStartAngleRef.current = radiusHandleAngle;
      arcDragStartClientRef.current = { x: e.clientX, y: e.clientY };
      arcDragInitialRef.current = null;
      setDragMode('arc');
      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ok */ }
    },
    [interactive, onDrawArc, radiusHandleAngle]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const start = dragStartRef.current;
      if (dragMode === 'center' && start && onMove) {
        const dx = (e.clientX - start.clientX) / displayScale;
        const dy = (e.clientY - start.clientY) / displayScale;
        onMove(start.x + dx, start.y + dy);
      } else if (dragMode === 'radius' && start?.centerClientX != null && start?.centerClientY != null && onRadiusChange) {
        const dx = (e.clientX - start.centerClientX) / displayScale;
        const dy = (e.clientY - start.centerClientY) / displayScale;
        let dist = Math.hypot(dx, dy) || 1;
        let angle = pointerAngleDeg(e.clientX, e.clientY, start.centerClientX, start.centerClientY);
        if (snapToGrid) {
          const angleRad = (angle * Math.PI) / 180;
          const pencilX = x + dist * Math.cos(angleRad);
          const pencilY = y + dist * Math.sin(angleRad);
          const sn = snapToGrid(pencilX, pencilY);
          dist = Math.hypot(sn.x - x, sn.y - y) || 1;
          angle = (Math.atan2(sn.y - y, sn.x - x) * 180) / Math.PI;
          if (angle < 0) angle += 360;
        }
        onRadiusChange(Math.max(RADIUS_MIN, Math.min(RADIUS_MAX, dist)));
        setRadiusHandleAngle(angle);
      } else if (dragMode === 'arc') {
        const { cx, cy } = getCenterClient();
        const currentPointerAngle = pointerAngleDeg(e.clientX, e.clientY, cx, cy);
        const startClient = arcDragStartClientRef.current;
        const moveThresholdPx = 8;
        if (startClient) {
          const dist = Math.hypot(e.clientX - startClient.x, e.clientY - startClient.y);
          if (dist < moveThresholdPx) return;
          arcDragStartClientRef.current = null;
          arcDragInitialRef.current = { compassAngle: radiusHandleAngle, pointerAngle: currentPointerAngle };
          arcDragUnwrappedRef.current = 0;
          arcDragPrevPointerRef.current = currentPointerAngle;
        }
        const initial = arcDragInitialRef.current;
        if (!initial) return;
        let delta = currentPointerAngle - arcDragPrevPointerRef.current;
        while (delta > 180) delta -= 360;
        while (delta <= -180) delta += 360;
        arcDragUnwrappedRef.current += delta;
        arcDragPrevPointerRef.current = currentPointerAngle;
        const newAngleUnwrapped = initial.compassAngle + arcDragUnwrappedRef.current;
        arcEndUnwrappedRef.current = newAngleUnwrapped;
        let displayAngle = newAngleUnwrapped % 360;
        if (displayAngle < 0) displayAngle += 360;
        setRadiusHandleAngle(displayAngle);
        onArcPreview?.({ cx: x, cy: y, radius, startDeg: arcStartAngleRef.current, endDeg: newAngleUnwrapped });
      }
    },
    [dragMode, onMove, onRadiusChange, onArcPreview, snapToGrid, displayScale, getCenterClient, radiusHandleAngle, x, y, radius]
  );

  const handlePointerUp = useCallback(() => {
    if (dragMode === 'arc') {
      onArcPreview?.(null);
      if (onDrawArc) {
        const start = arcStartAngleRef.current;
        const end = arcEndUnwrappedRef.current;
        if (Math.abs(end - start) >= 2) {
          onDrawArc(x, y, radius, start, end);
        }
      }
    }
    setDragMode(null);
    dragStartRef.current = null;
    arcDragStartClientRef.current = null;
    arcDragInitialRef.current = null;
  }, [dragMode, onDrawArc, onArcPreview, x, y, radius]);

  const size = radius * 2;
  const padding = 36;
  const box = size + padding * 2;
  const cx = padding + radius;
  const cy = padding + radius;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const pencilX = radius * Math.cos(toRad(radiusHandleAngle));
  const pencilY = radius * Math.sin(toRad(radiusHandleAngle));
  const r = radius;
  const L = Math.max(legLength, r / 2 + 2);
  const halfR = r / 2;
  const kSq = L * L - halfR * halfR;
  const k = kSq > 0 ? Math.sqrt(kSq) : 0;
  const griffX = halfR * (pencilX / r) - (pencilY / r) * k;
  const griffY = halfR * (pencilY / r) + (pencilX / r) * k;

  const griffLen = Math.hypot(griffX, griffY) || 1;
  const leg1PerpX = (-griffY / griffLen) * (LEG_WIDTH / 2);
  const leg1PerpY = (griffX / griffLen) * (LEG_WIDTH / 2);
  const dx2 = pencilX - griffX;
  const dy2 = pencilY - griffY;
  const leg2Len = Math.hypot(dx2, dy2) || 1;
  const leg2PerpX = (-dy2 / leg2Len) * (LEG_WIDTH / 2);
  const leg2PerpY = (dx2 / leg2Len) * (LEG_WIDTH / 2);

  return (
    <div
      ref={containerRef}
      className="absolute z-[18] touch-none select-none pointer-events-none"
      style={{
        left: 0,
        top: 0,
        width: box,
        height: box,
        transform: `translate(${x - box / 2}px, ${y - box / 2}px)`,
        pointerEvents: interactive ? 'auto' : 'none',
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      <svg width={box} height={box} className="block" style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id="zirkel-leg-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(203,213,224,0.95)" />
            <stop offset="50%" stopColor="rgba(148,163,184,0.9)" />
            <stop offset="100%" stopColor="rgba(100,116,139,0.85)" />
          </linearGradient>
          <linearGradient id="zirkel-griff-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(226,232,240,0.98)" />
            <stop offset="100%" stopColor="rgba(148,163,184,0.9)" />
          </linearGradient>
          <linearGradient id="zirkel-needle-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.98)" />
            <stop offset="100%" stopColor="rgba(203,213,224,0.9)" />
          </linearGradient>
          <linearGradient id="zirkel-pencil-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(165,243,252,0.98)" />
            <stop offset="100%" stopColor="rgba(56,189,248,0.9)" />
          </linearGradient>
        </defs>
        <g transform={`translate(${cx}, ${cy})`}>
          {/* Schenkel 1: Nadel – Griff (mit Winkel, plastisch) */}
          <path
            d={`M ${leg1PerpX} ${leg1PerpY} L ${griffX + leg1PerpX} ${griffY + leg1PerpY} L ${griffX - leg1PerpX} ${griffY - leg1PerpY} L ${-leg1PerpX} ${-leg1PerpY} Z`}
            fill="url(#zirkel-leg-gradient)"
            stroke="rgba(100,116,139,0.5)"
            strokeWidth={0.6}
          />
          {/* Schenkel 2: Griff – Bleistift (V-Form, plastisch) */}
          <path
            d={`M ${griffX + leg2PerpX} ${griffY + leg2PerpY} L ${pencilX + leg2PerpX} ${pencilY + leg2PerpY} L ${pencilX - leg2PerpX} ${pencilY - leg2PerpY} L ${griffX - leg2PerpX} ${griffY - leg2PerpY} Z`}
            fill="url(#zirkel-leg-gradient)"
            stroke="rgba(100,116,139,0.5)"
            strokeWidth={0.6}
          />

          {/* Radius-Chip zwischen Nadelspitze und Bleistift */}
          {radius >= 30 && (
            <g style={{ pointerEvents: 'none' }}>
              <rect
                x={pencilX * 0.35 - 23}
                y={pencilY * 0.35 - 10}
                width={46}
                height={20}
                rx={10}
                ry={10}
                fill="rgba(255,255,255,0.82)"
                stroke="rgba(148,163,184,0.4)"
                strokeWidth={0.8}
              />
              <text
                x={pencilX * 0.35}
                y={pencilY * 0.35 + 4}
                fill="rgba(30,41,59,0.95)"
                textAnchor="middle"
                fontSize={Math.max(11, Math.min(14, radius / 12))}
                fontWeight={600}
              >
                r ≈ {(radius / GRID_CELL_PX).toFixed(1)}
              </text>
            </g>
          )}

          {/* Einstechpunkt (Nadelspitze) im Zentrum */}
          <g
            transform="translate(0, 0)"
            style={{ cursor: onMove && interactive ? 'move' : 'default', pointerEvents: onMove && interactive ? 'auto' : 'none' }}
            onPointerDown={handleCenterDown}
          >
            <title>Einstechpunkt (Zentrum verschieben)</title>
            <path
              d="M0,-10 L4,6 L0,4 L-4,6 Z"
              fill="url(#zirkel-needle-gradient)"
              stroke="rgba(100,116,139,0.5)"
              strokeWidth={0.6}
            />
            <circle cx={0} cy={0} r={14} fill="transparent" />
          </g>

          {/* Bleistift (Radius + Startpunkt): Kegelspitze am Schenkelende */}
          <g
            transform={`translate(${pencilX}, ${pencilY}) rotate(${radiusHandleAngle})`}
            style={{ cursor: onRadiusChange && interactive ? 'grab' : 'default', pointerEvents: onRadiusChange && interactive ? 'auto' : 'none' }}
            onPointerDown={handleRadiusDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          >
            <title>Bleistift (Radius &amp; Startpunkt)</title>
            <path d="M0,0 L12,-3.5 L12,3.5 Z" fill="url(#zirkel-pencil-gradient)" stroke="rgba(56,189,248,0.6)" strokeWidth={0.5} />
            <circle cx={0} cy={0} r={14} fill="transparent" />
          </g>

          {/* Griff (Schenkel treffen sich): plastischer Drehknopf */}
          <g
            transform={`translate(${griffX}, ${griffY}) rotate(${radiusHandleAngle})`}
            style={{ cursor: onDrawArc && interactive ? 'grab' : 'default', pointerEvents: onDrawArc && interactive ? 'auto' : 'none' }}
            onPointerDown={handleArcDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          >
            <title>Griff (drehen = Kreisbogen zeichnen)</title>
            <ellipse cx={0} cy={0} rx={10} ry={6} fill="url(#zirkel-griff-gradient)" stroke="rgba(100,116,139,0.7)" strokeWidth={0.8} />
            {[0, 1, 2, 3, 4].map((i) => (
              <line key={i} x1={-8 + i * 4} y1={-3} x2={-8 + i * 4} y2={3} stroke="rgba(100,116,139,0.45)" strokeWidth={0.5} />
            ))}
            <circle cx={0} cy={0} r={14} fill="transparent" />
          </g>
        </g>
      </svg>
    </div>
  );
}
